import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Platform,
  KeyboardTypeOptions,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { Paprika } from '../../styles/theme';

interface AuthFieldProps {
  icon: React.ComponentProps<typeof Feather>['name'];
  label: string;
  value: string;
  onChangeText: (t: string) => void;
  /** Renders as a password field with a reveal (eye) toggle. */
  password?: boolean;
  /** Inline error message shown below the field; also flips the field to error styling. */
  error?: string | null;
  autoFocus?: boolean;
  editable?: boolean;
  keyboardType?: KeyboardTypeOptions;
  onSubmitEditing?: () => void;
}

/**
 * Paper-styled text field: leading icon, floating label, warm focus halo, and
 * an error state (red border + halo + message row). Mirrors the handoff Field.
 */
export const AuthField: React.FC<AuthFieldProps> = ({
  icon,
  label,
  value,
  onChangeText,
  password = false,
  error = null,
  autoFocus = false,
  editable = true,
  keyboardType,
  onSubmitEditing,
}) => {
  const [focused, setFocused] = useState(false);
  const [reveal, setReveal] = useState(false);
  const active = focused || value.length > 0;
  const hasError = !!error;

  const borderColor = hasError ? Paprika.destructive : focused ? Paprika.accent : Paprika.border;
  const iconColor = hasError ? Paprika.destructive : focused ? Paprika.accent : Paprika.textMuted;
  const labelColor = hasError ? Paprika.destructive : Paprika.textMuted;

  const halo = hasError
    ? { boxShadow: '0 0 0 3px #fdeeed' }
    : focused
      ? { boxShadow: `0 0 0 3px ${Paprika.brandTint}` }
      : undefined;

  return (
    <View>
      <View style={[styles.container, { borderColor }, Platform.OS === 'web' ? (halo as object) : null]}>
        <Feather name={icon} size={18} color={iconColor} style={styles.leadingIcon} />
        <View style={styles.inputWrap}>
          <Text
            style={[
              styles.label,
              { color: labelColor },
              active ? styles.labelActive : styles.labelResting,
            ]}
            pointerEvents="none"
          >
            {label}
          </Text>
          <TextInput
            style={[styles.input, active && styles.inputActive]}
            value={value}
            onChangeText={onChangeText}
            secureTextEntry={password && !reveal}
            autoFocus={autoFocus}
            editable={editable}
            keyboardType={keyboardType}
            autoCapitalize="none"
            autoCorrect={false}
            onSubmitEditing={onSubmitEditing}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
        </View>
        {password ? (
          <TouchableOpacity onPress={() => setReveal((r) => !r)} style={styles.trailing} hitSlop={8}>
            <Feather name={reveal ? 'eye-off' : 'eye'} size={18} color={Paprika.textMuted} />
          </TouchableOpacity>
        ) : null}
      </View>
      {hasError ? (
        <View style={styles.errorRow}>
          <Feather name="alert-triangle" size={14} color={Paprika.destructive} />
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 56,
    paddingHorizontal: 16,
    backgroundColor: Paprika.cardBg,
    borderRadius: 14,
    borderWidth: 1.5,
  },
  leadingIcon: {
    marginRight: 10,
  },
  inputWrap: {
    flex: 1,
    height: '100%',
    position: 'relative',
    justifyContent: 'center',
  },
  label: {
    position: 'absolute',
    left: 0,
    fontFamily: 'Inter-Regular',
  },
  labelResting: {
    fontSize: 15,
    top: '50%',
    marginTop: -10,
  },
  labelActive: {
    fontSize: 11,
    top: 7,
    letterSpacing: 0.2,
  },
  input: {
    width: '100%',
    height: '100%',
    padding: 0,
    fontFamily: 'Inter-Regular',
    fontSize: 15,
    color: Paprika.textPrimary,
    // RN web needs the outline explicitly removed
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as object) : null),
  },
  inputActive: {
    paddingTop: 16,
  },
  trailing: {
    marginLeft: 8,
    padding: 2,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    color: Paprika.destructive,
    lineHeight: 17,
  },
});
